import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  ComponentProps,
  useState,
} from "react";
import {twMerge} from "tailwind-merge";
import classNames from "classnames";
import {Error} from "../InputField/Error";
import {imageBaseUrl} from "../../lib/Utils/variables";

export interface FileUploadField extends ComponentProps<"input"> {
  id: string;
  error?: string;
  previewFile?: string;
  onValueChange?: (value: string, error?: boolean) => void;
}

const FileUploadField = forwardRef<{handleClear: () => void}, FileUploadField>(
  (
    {previewFile, children, error, onValueChange, className, ...restProps},
    InputRef,
  ) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [selectedFile, setSelectedFile] = useState<File>();
    const [preview, setPreview] = useState<{
      fileTypeImg: boolean;
      objectUrl: string;
    } | null>(
      previewFile
        ? {
            fileTypeImg: previewFile.includes("image") ? true : false,
            objectUrl: previewFile,
          }
        : null,
    );

    useImperativeHandle(InputRef, () => ({
      handleClear: () => {
        setSelectedFile(undefined);
        setPreview(null);
      },
    }));

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files?.length) {
        setSelectedFile(e.target.files[0]);
        const reader = new FileReader();
        reader.readAsDataURL(e.target.files[0]);
        reader.onload = () => {
          if (!(reader.result as string).includes("image")) {
            setPreview({
              fileTypeImg: false,
              objectUrl: reader.result as string,
            });
          } else {
            setPreview({
              fileTypeImg: true,
              objectUrl: reader.result as string,
            });
          }
          if (onValueChange) {
            return onValueChange(reader.result as string);
          }
        };
        reader.onerror = () => {
          if (onValueChange) {
            onValueChange("", true);
          }
        };
      }
      if (restProps.onChange) {
        return restProps.onChange(e);
      }
    };

    const removeSelection: React.MouseEventHandler<HTMLDivElement> =
      React.useCallback((e) => {
        if (e) {
          e.preventDefault();
        }
        if (fileInputRef.current) fileInputRef.current.value = "";
        setSelectedFile(undefined);
        setPreview(null);
        if (onValueChange) {
          onValueChange("");
        }
      }, []);
    return (
      <>
        {!preview?.objectUrl && (
          <>
            <input
              ref={fileInputRef}
              className="hidden"
              type="file"
              {...restProps}
              onChange={handleChange}
            />
            <label
              className={twMerge(
                `text-primaryBlue-700 cursor-pointer h-auto min-h-[10rem] w-64 border-[1.5px] 
                   border-dashed border-primary-300 bg-primary-50 flex flex-col justify-center items-center rounded-2xl`,
                className,
              )}
              htmlFor={restProps.id}
            >
              <div className="flex flex-col items-center justify-center text-xs">
                <div className={`flex justify-center ${preview ? "ml-4" : ""}`}>
                  <img
                    className={
                      preview ? "fill-primaryBlue-700 flex-1" : "bg-transparent"
                    }
                    src={
                      preview?.objectUrl
                        ? preview.objectUrl
                        : restProps.accept?.includes("image")
                        ? `${imageBaseUrl}/img/maya/svg/upload-kyc-docs/upload-icon.svg`
                        : `${imageBaseUrl}/img/maya/svg/upload-kyc-docs/doc-upload.svg`
                    }
                    width="100px"
                  />
                  {preview && <CloseIcon onClick={removeSelection} />}
                </div>
                {preview && (
                  <span className="mr-4 text-primaryBlue-700">
                    {selectedFile?.name}
                  </span>
                )}
              </div>
              {!preview && (
                <span className="mt-2 text-sm text-center">{children}</span>
              )}
              <Error
                className={classNames("grow-0 mx-3.5 text-center break-words", {
                  "pb-4": error,
                })}
              >
                {error}
              </Error>
            </label>
          </>
        )}
        {preview?.objectUrl && (
          <div className="flex w-64 h-40">
            <div className="flex-1 mt-4 overflow-hidden rounded">
              {preview.fileTypeImg && (
                <img src={preview.objectUrl} className="w-full h-full" />
              )}
              {!preview.fileTypeImg && (
                <iframe src={preview.objectUrl} width="100%" height="100%" />
              )}
            </div>
            <CloseIcon onClick={removeSelection} />
          </div>
        )}
      </>
    );
  },
);

const CloseIcon = ({onClick}: {onClick: React.MouseEventHandler}) => {
  return (
    <div
      className="w-5 text-xs text-center text-red-500 border-2 border-red-500 rounded-full cursor-pointer aspect-square h-max"
      onClick={onClick}
    >
      &#10005;
    </div>
  );
};

export const FileUpload = ({
  className,
  children,
  ...restProps
}: ComponentProps<"div">) => {
  return (
    <>
      <div
        className={twMerge(
          "flex flex-col gap-3 justify-center items-center",
          className,
        )}
        {...restProps}
      >
        {children}
      </div>
    </>
  );
};

FileUploadField.displayName = "FileUploadField";
FileUpload.Field = FileUploadField;
